import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SUPPORTED_LANGUAGES } from '@/i18n/config';
import { landingAdminApi } from '@/lib/landingApi';

export const LanguageFlagManager = () => {
  const [busyLocale, setBusyLocale] = useState(null);
  const [versions, setVersions] = useState({});

  const regenerate = async (locale) => {
    setBusyLocale(locale);
    try {
      const result = await landingAdminApi.regenerateFlag(locale);
      setVersions((current) => ({ ...current, [locale]: result.version || Date.now() }));
      toast.success(`${locale.toUpperCase()} flag regenerated.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Flag regeneration failed.');
    } finally {
      setBusyLocale(null);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="landing-language-flags">
      {SUPPORTED_LANGUAGES.map((language) => {
        const isBusy = busyLocale === language.code;
        const src = versions[language.code]
          ? `${language.flagUrl}?v=${versions[language.code]}`
          : language.flagUrl;
        return (
          <div
            key={language.code}
            className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-3"
            data-testid={`landing-flag-row-${language.code}`}
          >
            <div className="flex min-w-0 items-center gap-3">
              <img
                src={src}
                alt={`${language.label} flag`}
                className="h-9 w-9 shrink-0 rounded-sm border border-border object-cover"
                data-testid={`landing-flag-preview-${language.code}`}
              />
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold">{language.nativeLabel}</div>
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">{language.code}</div>
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={Boolean(busyLocale)}
              onClick={() => regenerate(language.code)}
              className="shrink-0 gap-1.5"
              data-testid={`landing-regenerate-flag-${language.code}`}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isBusy ? 'animate-spin' : ''}`} />
              {isBusy ? 'Generating…' : 'Regenerate flag'}
            </Button>
          </div>
        );
      })}
    </div>
  );
};